/**
 * Internationalization manager for FR/NL
 * Automatic language detection and persistence
 */

import React from "react";

export type Language = "fr" | "nl";

export type TranslationKey = string;
export type Translations = Record<string, string>;

class I18nManager {
  private translations: Record<Language, Translations> = {} as Record<
    Language,
    Translations
  >;
  private currentLanguage: Language = "fr";
  private isLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;
  private listeners: Set<(language: Language) => void> = new Set();

  constructor() {
    // Initialize language immediately from localStorage or detection
    this.currentLanguage = this.initializeLanguage();
    // Load translations asynchronously
    this.loadingPromise = this.loadTranslations();
  }

  /**
   * Loads translations from JSON files
   */
  async loadTranslations(): Promise<void> {
    if (this.isLoaded) {
      return;
    }

    try {
      // Dynamic import of translation files
      const [frTranslations, nlTranslations] = await Promise.all([
        import("../locales/fr.json"),
        import("../locales/nl.json"),
      ]);

      this.translations = {
        fr: frTranslations.default,
        nl: nlTranslations.default,
      };

      this.isLoaded = true;
    } catch (error) {
      console.error("Error loading translations:", error);
      this.isLoaded = true;
    }
  }

  /**
   * Ensures translations are loaded before use
   */
  async ensureLoaded(): Promise<void> {
    if (this.loadingPromise) {
      await this.loadingPromise;
    }
  }

  /**
   * Gets a translation for a given key with optional interpolation
   */
  getTranslation(
    key: string,
    language?: Language,
    variables?: Record<string, string | number>,
  ): string {
    const lang = language || this.currentLanguage;
    let translation = this.translations[lang]?.[key] || key;

    // Variable interpolation if provided
    if (variables && typeof translation === "string") {
      translation = this.interpolate(translation, variables);
    }

    return translation;
  }

  /**
   * Variable interpolation in translation strings
   * Replaces {variable} with corresponding value
   */
  private interpolate(
    template: string,
    variables: Record<string, string | number>,
  ): string {
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return variables[key]?.toString() || match;
    });
  }

  /**
   * Sets current language with persistence and URL redirect
   */
  setLanguage(language: Language): void {
    this.currentLanguage = language;
    // Persistence in localStorage
    try {
      localStorage.setItem("transmoove-language", language);
    } catch (error) {
      console.warn("Unable to save language to localStorage:", error);
    }

    // Redirect to /fr/ or /nl/ path
    try {
      const currentPath = window.location.pathname;
      const currentLangMatch = currentPath.match(/^\/(fr|nl)\//);
      const currentLang = currentLangMatch?.[1];

      // Only redirect if we're not already on the correct language path
      if (currentLang !== language) {
        window.location.href = `/${language}/`;
        return; // Return early since we're redirecting
      }
    } catch (error) {
      console.warn("Unable to redirect to language path:", error);
    }

    // Notify subscribers so all hooks update
    this.listeners.forEach((listener) => {
      try {
        listener(language);
      } catch (err) {
        console.warn("i18n listener error", err);
      }
    });
  }

  /**
   * Gets current language
   */
  getLanguage(): Language {
    return this.currentLanguage;
  }

  /**
   * Subscribe to language changes
   */
  subscribe(listener: (language: Language) => void): () => void {
    this.listeners.add(listener);
    // return unsubscribe
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Automatically detects browser language
   */
  detectBrowserLanguage(): Language {
    try {
      const browserLang = navigator.language.toLowerCase();

      // Check for Dutch (nl, nl-BE, nl-NL)
      if (browserLang.startsWith("nl")) {
        return "nl";
      }

      // Check for French (fr, fr-BE, fr-FR, fr-CA, etc.)
      if (browserLang.startsWith("fr")) {
        return "fr";
      }

      // Special cases for Belgium (can have nl or fr)
      if (browserLang.includes("be")) {
        // In Belgium, prioritize Dutch by default
        return "nl";
      }
    } catch (error) {
      console.warn("Unable to detect browser language:", error);
    }

    // Default to French
    return "fr";
  }

  /**
   * Initializes language from URL path (/fr/ or /nl/)
   */
  initializeLanguage(): Language {
    try {
      // 1. Check URL path first (highest priority: /fr/ or /nl/)
      const pathMatch = window.location.pathname.match(/^\/(fr|nl)\//);
      if (pathMatch) {
        const pathLang = pathMatch[1] as Language;
        // Save to localStorage for persistence
        localStorage.setItem("transmoove-language", pathLang);
        return pathLang;
      }

      // 2. If no lang in path, check localStorage
      const savedLanguage = localStorage.getItem(
        "transmoove-language",
      ) as Language;

      if (savedLanguage && (savedLanguage === "fr" || savedLanguage === "nl")) {
        return savedLanguage;
      }
    } catch (error) {
      console.warn("Unable to detect language:", error);
    }

    // 3. Auto-detect browser language as fallback
    const detectedLanguage = this.detectBrowserLanguage();

    // Save detected language
    try {
      localStorage.setItem("transmoove-language", detectedLanguage);
    } catch (error) {
      console.warn("Unable to save detected language:", error);
    }

    return detectedLanguage;
  }

  /**
   * Checks if translations are loaded
   */
  isTranslationsLoaded(): boolean {
    return this.isLoaded;
  }
}

// Singleton instance
export const i18nManager = new I18nManager();

// React hook to use translations with reactivity
export const useTranslation = (language: Language) => {
  return {
    t: (key: string, variables?: Record<string, string | number>) =>
      i18nManager.getTranslation(key, language, variables),
    language,
    isLoaded: i18nManager.isTranslationsLoaded(),
    ensureLoaded: () => i18nManager.ensureLoaded(),
  };
};

// Advanced React hook with state management
export const useI18n = () => {
  const [currentLanguage, setCurrentLanguage] = React.useState<Language>(
    i18nManager.getLanguage(),
  );
  const [isLoaded, setIsLoaded] = React.useState(
    i18nManager.isTranslationsLoaded(),
  );

  React.useEffect(() => {
    // Ensure translation loading
    i18nManager.ensureLoaded().then(() => {
      setIsLoaded(true);
    });

    // Subscribe to global language changes
    const unsubscribe = i18nManager.subscribe((lang) => {
      setCurrentLanguage(lang);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const changeLanguage = (newLanguage: Language) => {
    i18nManager.setLanguage(newLanguage);
    setCurrentLanguage(newLanguage);
  };

  const t = (key: string, variables?: Record<string, string | number>) => {
    return i18nManager.getTranslation(key, currentLanguage, variables);
  };

  return {
    language: currentLanguage,
    setLanguage: changeLanguage,
    t,
    isLoaded,
    detectBrowserLanguage: () => i18nManager.detectBrowserLanguage(),
  };
};
